import '../i18n/autoTranslate';
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import QuickActionButton from '../../components/ui/QuickActionButton';
import { ChevronDown, Info } from 'lucide-react';
import SpotlightCard from '../../components/ui/SpotlightCard';
import AppBackground from '../../components/ui/AppBackground.tsx';


import { Alert, AlertTitle, AlertDescription } from '../../components/ui/Alert';
import DotPattern from '../../components/lightswind/dot-pattern';
import Button from '../../components/ui/Button';
import { Badge } from '../../components/ui/shadcn/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/shadcn/collapsible';

// Import all components
import InterviewTypeCard from './components/InterviewTypeCard';
import RoleTargetingSection from './components/RoleTargetingSection';
import AIPersonalityCustomizer from './components/AIPersonalityCustomizer';
import QuestionConfiguration from './components/QuestionConfiguration';
import SessionFormatSelector from './components/SessionFormatSelector';
import ConfigurationSummary from './components/ConfigurationSummary';
import StepProgressIndicator from './components/StepProgressIndicator';
import InterviewSetupHowItWorksModal from './components/InterviewSetupHowItWorksModal';

import { API_BASE_URL } from '../../config/api';
import SEO from '../SEO';

const InterviewSetupConfiguration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  // Get interview type from URL params if coming from quick actions
  const urlParams = new URLSearchParams(location.search);
  const initialType = urlParams?.get('type') || 'behavioral';

  const [currentStep, setCurrentStep] = useState(0);
  const [configuration, setConfiguration] = useState({
    selectedType: initialType,
    selectedIndustry: '',
    selectedRole: '',
    selectedCompanyType: '',
    estimatedQuestions:1,
    personality: {
      selectedPersona: {},
      tone: 3,
      difficulty: 3,
      pace: 3,
      pressure: 3,
      accent: 'american'
    },
    selectedCategories: ['behavioral'],
    selectedCompanies: [],
    interviewType:initialType,
    customQuestions: [],
    enableFollowUps: true,
    surpriseQuestions: false,
    starMethodEnforcement: true,
    followUpIntensity: 3,
    adaptiveDifficulty: false,
    selectedFormat: 'phone',
    duration: 15,
    elevenlabsVoice:null
  });

  const steps = [
    { id: 'type', label: 'Interview Type', icon: "/message_2_jekkwq.png" },
    { id: 'targeting', label: 'Role Targeting', icon: "/bulleseye_czdi2g.png" },
    { id: 'personality', label: 'AI Personality', icon: "/ai_sjqlnq.png" },
    { id: 'questions', label: 'Questions', icon: "/question_yagabm.png" },
    { id: 'format', label: 'Format', icon: "/screen_btq7sx.png" },
    { id: 'summary', label: 'Review', icon: "/check_hdjdze.png" }
  ];
  
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    variant: "default",
    title: "",
    description: ""
  });

  // Auto-save configuration to localStorage
  const saveConfig=()=>{
    localStorage.setItem('finalInterviewConfiguration', JSON.stringify(configuration));
    // Show loading alert
    setAlertInfo({
      variant: "default",
      title: "Save",
      description: "Configuration's saved successfully.",
      show: true
    });
    
    setTimeout(()=>{
      setAlertInfo({
        variant: "default",
        title: "",
        description: "",
        show: false
      });
    },3000)
  }
  
  useEffect(() => {
    setConfiguration(prev => ({
      ...prev,
      interviewType: prev.selectedType
    }));
  }, [configuration.selectedType]);
  
  // Load saved configuration on mount
  useEffect(() => {
    const savedConfig = localStorage.getItem('finalInterviewConfiguration');
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        setConfiguration(prev => ({
          ...prev,
          ...parsed,
          selectedFormat: parsed?.selectedFormat === 'video' ? 'phone' : parsed?.selectedFormat
        }));
      } catch (error) {
        console.error('Failed to load saved configuration:', error);
      }
    }
  }, []);

  const handleNext = () => {
    if (currentStep < steps?.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  const handleStartInterview = async () => {
    localStorage.setItem(
      "finalInterviewConfiguration",
      JSON.stringify(configuration)
    );

    const showSetupAlert = (title, description, variant = "destructive") => {
      setAlertInfo({
        variant,
        title,
        description,
        show: true
      });

      setTimeout(() => {
        setAlertInfo({
          variant: "default",
          title: "",
          description: "",
          show: false
        });
      }, 5000);
    };

    try {
      const token =
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("access_token");

      if (!token) {
        navigate('/login');
        return;
      }

      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      };

      const canUseResponse = await fetch(`${API_BASE_URL}/api/subscriptions/use-interview`, {
        method: "POST",
        headers
      });

      const canUseResult = await canUseResponse.json().catch(() => ({}));

      if (!canUseResponse.ok || !canUseResult?.success) {
        const message =
          canUseResult?.error ||
          canUseResult?.message ||
          "Your subscription cannot start another interview right now.";

        // Only auth failures should go to login. Subscription/usage failures stay on the page.
        if (canUseResponse.status === 401) {
          showSetupAlert("Session expired", "Please log in again to continue.", "destructive");
          navigate('/login');
          return;
        }

        showSetupAlert(
          canUseResult?.reason === "subscription_expired"
            ? "Subscription ended"
            : canUseResult?.reason === "interview_limit_reached"
              ? "Interview limit reached"
              : "Interview not available",
          message,
          "destructive"
        );
        return;
      }

      setAlertInfo({
        variant: "default",
        title: "Interview slot reserved",
        description: `You have used ${canUseResult.interviews_used} out of ${canUseResult.interviews_limit} interviews this month. ${canUseResult.interviews_remaining} remaining.`,
        show: true
      });

      // Make POST request to create interview session
      const response = await fetch(`${API_BASE_URL}/api/interview/create`, {
        method: "POST",
        headers,
        body: JSON.stringify(configuration),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = result?.error || result?.message || `HTTP error! status: ${response.status}`;
        if (response.status === 401) {
          showSetupAlert("Session expired", "Please log in again to continue.", "destructive");
          navigate('/login');
          return;
        }
        showSetupAlert("Could not create interview", message, "destructive");
        return;
      }

      localStorage.setItem("current_session_id", result.session_id);
      localStorage.setItem("current_interview_id", result.interview_id);
      localStorage.setItem("chatInterviewAutoStart", configuration?.selectedFormat === "chat" ? "1" : "0");

      console.log("Interview created successfully:", result);

      const format = configuration?.selectedFormat;

      if (format === 'phone') {
        navigate('/phone-interview');
        return;
      }
      if (format === 'chat' && configuration?.selectedType !== 'technical') {
        // The slot is already reserved and the backend session is already created above.
        // Send the final configuration into ChatInterviewPage so it starts directly
        // instead of showing the chat page configuration wizard again.
        navigate('/chat-interview', {
          replace: true,
          state: {
            fromSetup: true,
            selectedFormat: 'chat',
            configuration,
            sessionId: result?.session_id,
            interviewId: result?.interview_id,
          },
        });
        return;
      }
      if (format === 'chat' && configuration?.selectedType === 'technical') {
        navigate("/tech-interview");
        return;
      }
      navigate("/live-interview-session");
    } catch (error) {
      console.error("Error creating interview session:", error);
      showSetupAlert(
        "Interview setup failed",
        error?.message || "Something went wrong. Please try again.",
        "destructive"
      );
    }
  };


  const handleEditSection = (section) => {
    const sectionMap = {
      'type': 0,
      'targeting': 1,
      'personality': 2,
      'questions': 3,
      'format': 4,
      'summary': 5
    };
    setCurrentStep(sectionMap?.[section] || 0);
  };

  const isStepDone = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return Boolean(configuration?.selectedType);
      case 1:
        return Boolean(configuration?.selectedIndustry && configuration?.selectedRole);
      case 2:
        return Boolean(configuration?.personality?.selectedPersona);
      case 3:
        return Boolean(configuration?.selectedCategories?.length > 0);
      case 4:
        return Boolean(configuration?.selectedFormat && configuration?.duration);
      case 5:
        return true;
      default:
        return false;
    }
  };

  const stepMeta = useMemo(() => {
    const list = [
      {
        id: 'type',
        title: 'Interview Type',
        description: 'Choose the style of interview you want to practice.',
        image: "/assets/images/Gemini_Generated_Image_d6peahd6peahd6pe_tsm1ya.png",
        spotlight: 'rgba(139, 92, 246, 0.2)'
      },
      {
        id: 'targeting',
        title: 'Role Targeting',
        description: 'Set your target role and company preferences.',
        image: "/assets/images/Gemini_Generated_6_dlmovh.png",
        spotlight: 'rgba(99, 102, 241, 0.2)'
      },
      {
        id: 'personality',
        title: 'AI Personality',
        description: 'Customize how the interviewer behaves.',
        image: "/assets/images/Gemini_Generated_6_dlmovh.png",
        spotlight: 'rgba(16, 185, 129, 0.2)'
      },
      {
        id: 'questions',
        title: 'Questions',
        description: 'Pick categories and tune follow-up settings.',
        image: "/assets/images/Gemini_Generated_6_dlmovh.png",
        spotlight: 'rgba(245, 158, 11, 0.2)'
      },
      {
        id: 'format',
        title: 'Format',
        description: 'Choose phone/chat and set session duration.',
        image: "/assets/images/Gemini_Generated_6_dlmovh.png",
        spotlight: 'rgba(99, 102, 241, 0.2)'
      },
      {
        id: 'summary',
        title: 'Review',
        description: 'Confirm everything before starting.',
        image: "/assets/images/Gemini_Generated_6_dlmovh.png",
        spotlight: 'rgba(139, 92, 246, 0.2)'
      }
    ];

    return {
      list,
      current: list[currentStep] || list[0]
    };
  }, [currentStep]);

  const isStepValid = () => {
    switch (currentStep) {
      case 0: // Interview Type
        return configuration?.selectedType;
      case 1: // Role Targeting
        return configuration?.selectedIndustry && configuration?.selectedRole;
      case 2: // AI Personality
        return configuration?.personality?.selectedPersona;
      case 3: // Questions
        return configuration?.selectedCategories?.length > 0;
      case 4: // Format
        return configuration?.selectedFormat && configuration?.duration;
      case 5: // Summary
        return true;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 auto-rows-fr">
            {['behavioral', 'technical', 'case_study', 'stress', 'panel']?.map((type) => (
              <InterviewTypeCard
                key={type}
                type={type}
                isSelected={configuration?.selectedType === type}
                onSelect={(selectedType) =>
                  setConfiguration(prev => ({ ...prev, selectedType }))
                }
              />
            ))}
          </div>
        );

      case 1:
        return (
          <RoleTargetingSection
            embedded
            selectedIndustry={configuration?.selectedIndustry}
            onIndustryChange={(selectedIndustry) => 
              setConfiguration(prev => ({ ...prev, selectedIndustry }))
            }
            selectedCompanyType={configuration?.selectedCompanyType}
            onCompanyTypeChange={(selectedCompanyType) => 
              setConfiguration(prev => ({ ...prev, selectedCompanyType }))
            }
            selectedRole={configuration?.selectedRole}
            onRoleChange={(selectedRole) => 
              setConfiguration(prev => ({ ...prev, selectedRole }))
            }
            selectedCompaniesAttr={configuration?.selectedCompanies}
            onCompaniesChange={(companies) => 
              setConfiguration(prev => ({ ...prev, selectedCompanies: companies }))
            }
          />
        );

      case 2:
        return (
          <AIPersonalityCustomizer
            embedded
            personality={configuration?.personality}
            onPersonalityChange={(updatedPersonality) => 
              setConfiguration(prev => ({ 
                ...prev, 
                personality: updatedPersonality,
                elevenlabsVoice: updatedPersonality?.selectedPersona?.elevenlabs.voice_id || prev.personality?.elevenlabsVoice || null
              }))
            }
          />
        );

      case 3:
        return (
          <QuestionConfiguration
            configuration={configuration}
            onConfigurationChange={setConfiguration}
            embedded
          />
        );

      case 4:
        return (
          <SessionFormatSelector
            embedded
            selectedFormat={configuration?.selectedFormat}
            onFormatChange={(selectedFormat) => 
              setConfiguration(prev => ({ ...prev, selectedFormat }))
            }
            duration={configuration?.duration}
            onDurationChange={(duration) => 
              setConfiguration(prev => ({ ...prev, duration }))
            }
          />
        );

      case 5:
        return (
          <ConfigurationSummary
            saveConfig={saveConfig}
            configuration={configuration}
            onStartInterview={handleStartInterview}
            onEditSection={handleEditSection}
            onConfigurationChange={setConfiguration}
            embedded
          />
        );

      default:
        return null;
    }
  };

  return (
    <AppBackground className="min-h-screen bg-[#0B0B12] text-white overflow-hidden">
    <SEO
      title="AI Interviews — Practice Realistic Job Interviews with InterviewGenieAI"
      description="Practice realistic AI interviews tailored to your job role. Answer by voice or chat, receive instant feedback, improve your confidence, and prepare for real interviews."
      canonical="https://interviewgenieai.com/ai-interviews"
    />
      <main className="relative z-10 pt-6 pb-24 md:pb-8">
        <InterviewSetupHowItWorksModal
          open={showHowItWorks}
          onClose={() => setShowHowItWorks(false)}
          selectedFormat={configuration?.selectedFormat}
        />
        {alertInfo.show && (
          <div style={{zIndex:999999999}} className="fixed top-20 right-4 z-50 max-w-md">
            <Alert variant={alertInfo.variant}>
              <AlertTitle>{alertInfo.title}</AlertTitle>
              <AlertDescription>{alertInfo.description}</AlertDescription>
            </Alert>
          </div>
        )}
                <DotPattern className="opacity-15" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            <SpotlightCard spotlightColor="rgba(135, 91, 237, 0.35)" className="custom-spotlight-card rounded-3xl">
              <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[rgba(30,30,46,0.75)] via-[rgba(30,30,46,0.55)] to-[rgba(46,46,69,0.30)] p-6 sm:p-8">
                {/* <div className="absolute inset-0 opacity-10 pointer-events-none">
                <img src={"/assets/images/Gemini_Generated_2_zckc1w.png"} className="absolute inset-0 h-full w-full object-cover scale-110 " />
                  <div className="absolute inset-0 bg-gradient-to-br from-black/5 via-black/60 to-black/80" />
                </div> */}
                <div className="relative">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      {/* How it works button (above the title) */}
                      <button
                        type="button"
                        onClick={() => setShowHowItWorks(true)}
                        className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70 shadow-[0_0_0_1px_rgba(255,255,255,0.03)] backdrop-blur-xl hover:bg-white/[0.07] transition"
                      >
                        <Info className="h-4 w-4 text-white/70" />
                        How it works
                        <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/70">
                          End-to-end
                        </span>
                      </button>
                      <h1 className="text-2xl sm:text-4xl font-semibold tracking-tight text-white">Interview Setup</h1>
                      <p className="text-sm sm:text-base text-gray-200/80">Configure your session in a few quick steps.</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-2">
                      <Badge variant="outline" className="bg-white/10 border-white/15 text-white">
                        Step {currentStep + 1} / {steps.length}
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-3 sm:p-4 overflow-hidden">
                    <StepProgressIndicator
                      currentStep={currentStep}
                      totalSteps={steps?.length}
                      steps={steps}
                      onStepClick={handleStepClick}
                    />
                  </div>
                </div>
              </div>
            </SpotlightCard>

            <SpotlightCard spotlightColor="rgba(99, 102, 241, 0.35)" className="custom-spotlight-card rounded-3xl">
              <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[rgba(30,30,46,0.75)] via-[rgba(30,30,46,0.55)] to-[rgba(46,46,69,0.30)] p-2">
                {/* <div className="absolute inset-0 opacity-25 pointer-events-none">
                  <img src={stepMeta.current.image} className="absolute inset-0 h-full w-full object-cover scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-black/55 to-black/75" />
                </div> */}
                <div className="relative p-6 sm:p-8">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xl sm:text-2xl font-semibold tracking-tight text-white">{stepMeta.current.title}</div>
                      <Badge variant="outline" className="bg-white/10 border-white/15 text-white">
                        Step {currentStep + 1} / {steps.length}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-200/70">{stepMeta.current.description}</div>
                  </div>

                  <div className="mt-6">
                    {renderStepContent()}
                  </div>

                  {currentStep < steps.length - 1 && (
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                      <Button
                        variant="ghost"
                        size="lg"
                        iconName="ArrowLeft"
                        iconPosition="left"
                        onClick={handlePrevious}
                        disabled={currentStep === 0}
                        className="w-full sm:flex-1 text-gray-200 hover:text-white hover:bg-white/5"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="default"
                        size="lg"
                        iconName="ArrowRight"
                        iconPosition="right"
                        onClick={handleNext}
                        disabled={!isStepValid()}
                        className="w-full sm:flex-1"
                      >
                        {currentStep === steps.length - 2 ? 'Review Setup' : 'Continue'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </SpotlightCard>

          </div>
        </div>
      </main>
      <div className="hidden xl:block fixed right-8 bottom-8 w-80 z-20">
        <SpotlightCard spotlightColor="rgba(99, 102, 241, 0.35)" className="custom-spotlight-card rounded-3xl">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[rgba(30,30,46,0.82)] via-[rgba(30,30,46,0.60)] to-[rgba(46,46,69,0.32)] p-4">
            <div className="absolute inset-0 opacity-25 pointer-events-none">
              <img
                src={"/assets/images/Gemini_Generated_6_dlmovh.png"}
                className="absolute inset-0 h-full w-full object-cover scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-black/55 to-black/75" />
            </div>

            <Collapsible open={!collapsed} onOpenChange={(open) => setCollapsed(!open)}>
              <div className="relative flex items-center justify-between gap-3">
                <div className="space-y-0.5">
                  <div className="text-sm font-semibold text-white">Quick Tips</div>
                  <div className="text-xs text-gray-200/70">{steps[currentStep]?.label}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-white/10 border-white/15 text-white">
                    Step {currentStep + 1}/{steps.length}
                  </Badge>
                  <CollapsibleTrigger asChild>
                    <button type="button" className="p-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
                      <ChevronDown className={`h-4 w-4 text-white transition-transform duration-300 ${collapsed ? '-rotate-180' : 'rotate-0'}`} />
                    </button>
                  </CollapsibleTrigger>
                </div>
              </div>
              <CollapsibleContent>
                <div className="relative mt-4 space-y-2 text-sm text-gray-200/75">
                  {currentStep === 0 && (
                    <>
                      <div>Behavioral interviews focus on STAR-style responses.</div>
                      <div>Technical interviews emphasize problem-solving and fundamentals.</div>
                      <div>Panel interviews test consistency across multiple interviewers.</div>
                    </>
                  )}
                  {currentStep === 1 && (
                    <>
                      <div>Pick an industry and role that match your target job.</div>
                      <div>Company type helps shape interview expectations.</div>
                      <div>Select companies to tailor the tone and examples.</div>
                    </>
                  )}
                  {currentStep === 2 && (
                    <>
                      <div>Increase difficulty gradually as your confidence grows.</div>
                      <div>Pace controls how quickly the interviewer moves on.</div>
                      <div>Pressure simulates real-world interview intensity.</div>
                    </>
                  )}
                  {currentStep === 3 && (
                    <>
                      <div>Choose categories aligned to your upcoming interview.</div>
                      <div>Enable follow-ups for deeper, more realistic sessions.</div>
                      <div>Add custom questions to practice specific scenarios.</div>
                    </>
                  )}
                  {currentStep === 4 && (
                    <>
                      <div>Phone gives voice-focused practice and pacing feedback.</div>
                      <div>Chat is ideal for quick iterations and reviewable answers.</div>
                      <div>Pick a duration that matches your real interview format.</div>
                    </>
                  )}
                  {currentStep === 5 && (
                    <>
                      <div>Review everything once before starting the session.</div>
                      <div>Save your configuration to reuse it later.</div>
                      <div>Edit any section instantly from the review step.</div>
                    </>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </SpotlightCard>
      </div>
      <QuickActionButton isVisible={false} onQuickStart={() => {}} />
    </AppBackground>
  );
};

export default InterviewSetupConfiguration;
